/**
 * AssetPicker — browse and pick an existing asset from the tenant media library
 * (tenant_<slug>.wp_assets, the SAME store the Assets & Media tab and the
 * builder's uploads write to). Used by ImageUpload's "Browse library" button so
 * images uploaded once can be reused across pages instead of re-uploaded.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { webPresenceApi } from '../../shared/services/platformApi';

type LibAsset = { id?: string; url: string; name: string; type?: string };

function normalize(rows: any[]): LibAsset[] {
  return (rows || [])
    .map((a) => ({
      id: a.id,
      url: a.cdn_url || a.storage_url || a.url || '',
      name: a.file_name || a.name || 'asset',
      type: a.file_type || a.type || '',
    }))
    .filter((a) => a.url && !a.url.startsWith('/storage/')); // skip legacy placeholder URLs
}

export function AssetPicker({ onPick, onClose }: { onPick: (url: string) => void; onClose: () => void }) {
  const [assets, setAssets] = useState<LibAsset[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancel = false;
    webPresenceApi
      .getAssets({ asset_type: 'image', limit: 100 } as any)
      .then((r: any) => {
        if (cancel) return;
        const rows = r?.data ?? r ?? [];
        setAssets(normalize(Array.isArray(rows) ? rows : []));
      })
      .catch(() => !cancel && setError('Could not load your media library.'));
    return () => { cancel = true; };
  }, []);

  const filtered = (assets || []).filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase()));

  return createPortal(
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,21,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, width: 'min(760px, 96vw)', maxHeight: '84vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid #eef0f3' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#14171c' }}>Media library</span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search images…"
            style={{ marginLeft: 'auto', width: 220, border: '1px solid #d7dbe0', borderRadius: 8, padding: '6px 10px', fontSize: 13 }}
          />
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 18, overflowY: 'auto' }}>
          {error && <p style={{ fontSize: 13, color: '#dc2626' }}>{error}</p>}
          {!assets && !error && <p style={{ fontSize: 13, color: '#9aa3af' }}>Loading…</p>}
          {assets && !filtered.length && !error && (
            <p style={{ fontSize: 13, color: '#9aa3af' }}>
              No images in your library yet. Upload one here or in Assets &amp; Media — it’ll then be reusable on any page.
            </p>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12 }}>
            {filtered.map((a) => (
              <button
                key={a.id || a.url}
                onClick={() => { onPick(a.url); onClose(); }}
                title={a.name}
                style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 6, border: '1px solid #e6e8ec', borderRadius: 10, background: '#fff', cursor: 'pointer', textAlign: 'left' }}
              >
                <span style={{ display: 'block', height: 90, borderRadius: 7, background: `#faf9ff url(${JSON.stringify(a.url)}) center/contain no-repeat` }} />
                <span style={{ fontSize: 11.5, color: '#4b5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
